import { useCallback, useMemo } from "react";
import { type CampaignAutopilotConfigV2Data } from "@cede/types";
import { QueryKeys, getQuoteToken, effectiveTradeSize, estimateCycles } from "@cede/utils";
import { getWalletTypesForExchange } from "@/hooks/portfolio/portfolioBalanceQuery";
import { useHubUser } from "@/hooks/user/useHubUser";
import { getExchangeAccountBalances, getMinimumRequiredBalance } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";
import Decimal from "decimal.js";

interface UseAutomaticStreakBalanceParams {
  accountId: string | undefined;
  exchangeId: string | undefined;
  pairSymbol: string | undefined;
  enabled?: boolean;
  /** V2 autopilot config - when provided, enables V2-specific balance calculations */
  autopilotConfigV2?: CampaignAutopilotConfigV2Data;
  /** Campaign name - required for V2 minimum safe balance API call */
  campaignName?: string;
  /** Epoch ID - required for V2 minimum safe balance API call */
  epochId?: number;
  /** Total target volume - required for V2 minimum safe balance API call */
  totalTargetVolume?: number;
}

// Buffer per cycle for spread/slippage losses (0.5% per cycle)
// This accounts for bid-ask spread and slippage on each buy+sell round-trip
const SPREAD_SLIPPAGE_PER_CYCLE_PERCENT = new Decimal("0.005");

// Base buffer for price fluctuations and exchange fees (5%)
// Covers typical CEX fees (~0.1-0.2%) plus safety margin for price volatility
const BASE_BUFFER_PERCENT = new Decimal("0.05");

export const useAutomaticStreakBalance = ({
  accountId,
  exchangeId,
  pairSymbol,
  enabled = true,
  autopilotConfigV2,
  campaignName,
  epochId,
  totalTargetVolume,
}: UseAutomaticStreakBalanceParams) => {
  const { auth, authenticated } = useHubUser();

  // Extract quote token from pair symbol (e.g., "SEDA/USDT" -> "USDT", "BTC/USDT:USDC" -> "USDT")
  const quoteToken = useMemo(() => {
    if (!pairSymbol) return undefined;
    return getQuoteToken(pairSymbol);
  }, [pairSymbol]);

  const query = useQuery({
    queryKey: [QueryKeys.AUTOMATIC_STREAK_BALANCE, accountId, exchangeId, quoteToken],
    queryFn: async () => {
      if (!accountId || !quoteToken) return null;

      const walletTypes = getWalletTypesForExchange(exchangeId ?? "");
      const groupedBalances = await getExchangeAccountBalances(auth, accountId, walletTypes);

      const accountBalances = groupedBalances[accountId];
      if (!accountBalances || !accountBalances[quoteToken]) {
        return { balance: 0, quoteToken };
      }

      return {
        balance: accountBalances[quoteToken].freeBalance || 0,
        quoteToken,
      };
    },
    enabled: enabled && authenticated && !!accountId && !!quoteToken,
    staleTime: 30 * 1000, // 30 seconds - balances can change
    gcTime: 2 * 60 * 1000, // 2 minutes cache
    refetchOnWindowFocus: true,
  });

  // V2: Fetch minimum required balance from the API
  // This replaces the local computation with the server-side calculation
  // that also accounts for existing active autopilots on the same exchange account
  const minimumRequiredBalanceQuery = useQuery({
    queryKey: [
      QueryKeys.AUTOMATIC_STREAK_MINIMUM_REQUIRED_BALANCE,
      campaignName,
      epochId,
      accountId,
      totalTargetVolume,
    ],
    queryFn: async () => {
      if (!campaignName || epochId == null || !accountId || !totalTargetVolume) return null;

      const result = await getMinimumRequiredBalance(auth, {
        campaignName,
        epochId,
        exchangeAccountId: Number(accountId),
        totalTargetVolume: String(totalTargetVolume),
      });

      return result.minimumRequiredBalance;
    },
    enabled:
      enabled &&
      authenticated &&
      !!campaignName &&
      epochId != null &&
      !!accountId &&
      !!totalTargetVolume &&
      totalTargetVolume > 0,
    staleTime: 30 * 1000,
    gcTime: 2 * 60 * 1000,
  });

  // Calculate max available volume per trade
  // Since trades are sequential (buy->sell->buy->sell), we only need funds for ONE trade at a time
  // But we add buffers for spread/slippage and price volatility
  const getMaxVolumePerTrade = (totalTrades: number): number => {
    if (!query.data?.balance || totalTrades <= 0) return 0;

    const balance = new Decimal(query.data.balance);
    const trades = new Decimal(totalTrades);

    // Calculate cumulative spread/slippage buffer across all cycles
    const cumulativeSpreadBuffer = SPREAD_SLIPPAGE_PER_CYCLE_PERCENT.mul(trades);

    // Total buffer = base buffer (5%) + spread/slippage buffer (0.5% per cycle)
    const totalBuffer = BASE_BUFFER_PERCENT.plus(cumulativeSpreadBuffer);

    // Max volume = balance / (1 + totalBuffer)
    // Floor to 2 decimal places to ensure the displayed value always passes validation
    // (avoids issues where display rounding could cause required balance to exceed actual balance)
    return balance.div(new Decimal(1).plus(totalBuffer)).toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber();
  };

  // Check if user has enough balance for the configured volume
  // Since trades are sequential, we need: volumeMax + buffers for spread/slippage and volatility
  const hasEnoughBalance = (volumeMax: number, totalTrades: number): boolean => {
    if (!query.data?.balance) return false;

    const balance = new Decimal(query.data.balance);
    const volume = new Decimal(volumeMax);
    const trades = new Decimal(totalTrades);

    // Buffers: base (5%) + spread/slippage (0.5% per cycle)
    const spreadSlippageBuffer = volume.mul(SPREAD_SLIPPAGE_PER_CYCLE_PERCENT).mul(trades);
    const baseBuffer = volume.mul(BASE_BUFFER_PERCENT);

    const requiredBalance = volume.plus(spreadSlippageBuffer).plus(baseBuffer);
    return balance.gte(requiredBalance);
  };

  // Calculate the required balance for display purposes
  const getRequiredBalance = (volumeMax: number, totalTrades: number): number => {
    const volume = new Decimal(volumeMax);
    const trades = new Decimal(totalTrades);

    const spreadSlippageBuffer = volume.mul(SPREAD_SLIPPAGE_PER_CYCLE_PERCENT).mul(trades);
    const baseBuffer = volume.mul(BASE_BUFFER_PERCENT);

    return volume.plus(spreadSlippageBuffer).plus(baseBuffer).toNumber();
  };

  // ============================================================================
  // V2-specific methods
  // In V2, funds are recycled, so we only need balance for one cycle (maxTradeSize)
  // However, fees accumulate over cycles, so we add a per-cycle fee buffer
  // ============================================================================

  /**
   * Get the balance-adjusted maxTradeSize for V2: the configured max capped by
   * the user's usable balance. Falls back to the configured max until balances load.
   */
  const getBalanceAdjustedMaxTradeSizeV2 = (): number => {
    if (!autopilotConfigV2) return 0;
    const configMaxTradeSize = autopilotConfigV2.maxTradeSize;
    // Only fall back to the config max while the balance is still loading; a real
    // zero balance flows through the cap (=> a zero trade size), not the max.
    if (query.data?.balance == null) return configMaxTradeSize;
    return effectiveTradeSize(configMaxTradeSize, Number(query.data.balance));
  };

  /**
   * Estimate the number of cycles to fill a target volume, using the
   * balance-adjusted trade size. Shared with the Solo CMS simulation.
   */
  const estimateCyclesV2 = (targetVol: number): number => {
    if (!autopilotConfigV2) return 0;
    return estimateCycles(targetVol, autopilotConfigV2.minTradeSize, getBalanceAdjustedMaxTradeSizeV2());
  };

  /**
   * Check if user has enough balance for V2 autopilot configuration.
   * Returns true if balance >= minimumRequiredBalance (from API).
   */
  const hasEnoughBalanceV2 = useCallback((): boolean => {
    if (!query.data?.balance || !autopilotConfigV2) return false;

    const minimumRequired = minimumRequiredBalanceQuery.data;
    if (minimumRequired == null) return false; // Not yet loaded

    const balance = new Decimal(query.data.balance);
    return balance.gte(new Decimal(minimumRequired));
  }, [query.data?.balance, autopilotConfigV2, minimumRequiredBalanceQuery.data]);

  return {
    balance: query.data?.balance ?? 0,
    quoteToken: query.data?.quoteToken ?? quoteToken,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isMinimumBalanceLoading: minimumRequiredBalanceQuery.isLoading,
    error: query.error?.message,
    // V1 methods
    getMaxVolumePerTrade,
    hasEnoughBalance,
    getRequiredBalance,
    // V2 methods
    estimateCyclesV2,
    requiredBalanceV2: minimumRequiredBalanceQuery.data ?? 0,
    hasEnoughBalanceV2,
    refetch: query.refetch,
  };
};
